import { useState, useEffect } from "react";
import { RankingPlayer } from "types/ranking";
import {
  getRankingWithPlayers,
  getPlayerRankings,
  getAllLeaderboards,
} from "services/ranking.service";
import { useAuth } from "context/AuthContext";

interface UseHomeRankingReturn {
  topPlayers: RankingPlayer[];
  allPlayers: RankingPlayer[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook customizado para buscar dados do ranking da home do jogador
 * Busca o primeiro ranking onde o usuário é participante
 * @returns Dados do ranking, estados de loading e erro
 */
export function useHomeRanking(): UseHomeRankingReturn {
  const { user } = useAuth();
  const [topPlayers, setTopPlayers] = useState<RankingPlayer[]>([]);
  const [allPlayers, setAllPlayers] = useState<RankingPlayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRankingData = async () => {
    if (!user) {
      setIsLoading(false);
      setError("Usuário não autenticado");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Usar email ou _id como identificador
      const userId = user.email || user._id;
      console.log("🔍 Buscando rankings para usuário:", { userId, user });

      // 1. Buscar rankings onde o usuário é participante
      const userRankings = await getPlayerRankings(userId);
      console.log("📊 Rankings do usuário:", userRankings);

      if (!userRankings || userRankings.length === 0) {
        console.log(
          "⚠️ Nenhum ranking encontrado para o usuário, buscando todos os leaderboards..."
        );
        // Se não houver rankings, buscar todos os leaderboards
        const allLeaderboards = await getAllLeaderboards();
        console.log("📋 Leaderboards disponíveis:", allLeaderboards);

        if (allLeaderboards.length > 0) {
          // Pegar o primeiro leaderboard disponível
          const firstLeaderboard = allLeaderboards[0];
          const rankingId = firstLeaderboard.extra?._id;
          console.log("✅ Usando leaderboard:", {
            id: rankingId,
            title: firstLeaderboard.title,
          });

          if (rankingId) {
            const players = await getRankingWithPlayers(rankingId);
            console.log("👥 Jogadores encontrados:", players.length);
            setAllPlayers(players);
            setTopPlayers(players.slice(0, 3));
            setIsLoading(false);
            return;
          }
        }

        console.log("❌ Nenhum ranking disponível");
        setError("Nenhum ranking disponível no momento");
        setIsLoading(false);
        return;
      }

      // 2. Pegar o primeiro ranking
      const firstRanking = userRankings[0];

      if (!firstRanking._id) {
        setError("Ranking inválido");
        setIsLoading(false);
        return;
      }

      // 3. Buscar jogadores do ranking
      const players = await getRankingWithPlayers(firstRanking._id);
      console.log("✅ Ranking carregado com sucesso:", {
        rankingId: firstRanking._id,
        totalPlayers: players.length,
        top3: players.slice(0, 3).map((p) => p.name),
      });

      // 4. Separar top 3 e todos os jogadores
      setTopPlayers(players.slice(0, 3));
      setAllPlayers(players);

      setIsLoading(false);
    } catch (err: any) {
      console.error("❌ Erro ao buscar dados do ranking:", err);
      setError(err.message || "Erro ao carregar ranking");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRankingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.email]);

  return {
    topPlayers,
    allPlayers,
    isLoading,
    error,
    refetch: fetchRankingData,
  };
}
